import React, { useEffect, useRef, useState } from 'react';
import { api } from '../../api.js';
import { loadProjectTasks } from './ProjectFixChecklist.js';
import { fixActivityRows, repoWebUrl, type FixActivityRow } from './fixProgress.js';
import { executeRemediationTask } from '../../features/remediation/services/remediationApi.js';

export function ProjectFixActivityContent({ rows, repoUrl, loading, error, onRefresh, onRetry, retryingId, retryError }: {
  rows: FixActivityRow[];
  repoUrl: string | null;
  loading: boolean;
  error: boolean;
  onRefresh: () => void;
  onRetry?: (taskId: string) => void;
  retryingId?: string | null;
  retryError?: string | null;
}): React.ReactElement {
  return <section aria-label="Fix progress" style={{ padding: 16, border: '1px solid var(--border, #ddd)', borderRadius: 10, margin: '12px 0' }}>
    <h2>Fix progress</h2>
    <p style={{ fontSize: 13 }}>Current status from your project. This stays available when you reopen the chat.</p>
    {loading && <p role="status">Loading fix progress…</p>}
    {error && <p role="alert">Could not refresh fix progress. The status below may be out of date.</p>}
    {retryError && <p role="alert">{retryError}</p>}
    {!loading && !error && rows.length === 0 && <p>No fixes have started for the current issues.</p>}
    {rows.map((row: FixActivityRow) => <div key={row.id} data-fix-state={row.state} style={{ margin: '10px 0' }}>
      <strong>{row.title}</strong><br />
      <span>{row.detail}</span>
      {row.state === 'paused' && onRetry && <button type="button" disabled={loading || error || Boolean(retryingId)}
        onClick={() => onRetry(row.id)}>{retryingId === row.id ? 'Retrying reconciliation…' : 'Retry reconciliation'}</button>}
      {row.pr && <>{' '}{repoUrl
        ? <a href={`${repoUrl}/pull/${row.pr}`} target="_blank" rel="noopener noreferrer">Pull request #{row.pr}</a>
        : <span>Pull request #{row.pr}</span>}</>}
    </div>)}
    <button type="button" onClick={onRefresh}>Refresh fix progress</button>
  </section>;
}

/** Durable progress recovery. Only an explicit paused-run retry requests recovery. */
export function ProjectFixActivity({ projectId, refreshKey, onOutcomeChange }: {
  projectId: string;
  refreshKey: number;
  onOutcomeChange: () => void;
}): React.ReactElement {
  const [rows, setRows] = useState<FixActivityRow[]>([]);
  const [repoUrl, setRepoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [retryError, setRetryError] = useState<string | null>(null);
  const loadedProjectRef = useRef<string | null>(null);
  const projectRef = useRef(projectId);
  projectRef.current = projectId;
  const onOutcomeChangeRef = useRef(onOutcomeChange);
  onOutcomeChangeRef.current = onOutcomeChange;
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let signature: string | null = null;
    const deadline = Date.now() + 30 * 60_000;
    setLoading(true);
    if (loadedProjectRef.current !== projectId) {
      setRows([]);
      setRepoUrl(null);
      loadedProjectRef.current = projectId;
    }
    setError(false);
    setRetryError(null);
    setRetryingId(null);
    void api.getProject(projectId).then((project) => {
      if (!cancelled) setRepoUrl(repoWebUrl((project as unknown as { repo?: { owner?: string | null; name?: string | null } }).repo));
    }).catch(() => { /* task state remains readable even if the repository lookup fails */ });
    const read = async (): Promise<void> => {
      if (cancelled || Date.now() >= deadline) return;
      if (document.hidden) {
        timer = setTimeout(() => { void read(); }, Math.min(30_000, deadline - Date.now()));
        return;
      }
      let active = false;
      try {
        const tasks = await loadProjectTasks(projectId);
        if (cancelled) return;
        const next = fixActivityRows(tasks);
        const nextSignature = JSON.stringify(next);
        if (signature !== null && signature !== nextSignature) onOutcomeChangeRef.current();
        signature = nextSignature;
        active = next.some((row: FixActivityRow) => row.state === 'running' || row.state === 'merging');
        setRows(next);
        setError(false);
      } catch {
        if (cancelled) return;
        setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
      if (!cancelled && Date.now() < deadline) timer = setTimeout(() => { void read(); }, Math.min(active ? 10_000 : 30_000, deadline - Date.now()));
    };
    void read();
    return () => { cancelled = true; if (timer !== null) clearTimeout(timer); };
  }, [projectId, refreshKey, retry]);
  const retryReconciliation = async (taskId: string): Promise<void> => {
    if (retryingId || loading || error || !rows.some((row) => row.id === taskId && row.state === 'paused')) return;
    const requestedProject = projectId;
    setRetryingId(taskId);
    setRetryError(null);
    try {
      await executeRemediationTask(taskId);
      if (projectRef.current !== requestedProject) return;
      onOutcomeChangeRef.current();
      setRetry((value: number) => value + 1);
    } catch {
      if (projectRef.current === requestedProject) setRetryError('Could not retry reconciliation. Refresh the progress and try again.');
    } finally {
      if (projectRef.current === requestedProject) setRetryingId(null);
    }
  };
  return <ProjectFixActivityContent rows={rows} repoUrl={repoUrl} loading={loading} error={error}
    retryingId={retryingId} retryError={retryError} onRetry={(taskId: string) => { void retryReconciliation(taskId); }}
    onRefresh={() => { onOutcomeChangeRef.current(); setRetry((value: number) => value + 1); }} />;
}
